
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { category, PromptTemplate, promtTemplatePlaceholders } from '@reflact/ipkv-types';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Breadcrumb } from "primeng/breadcrumb";
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from "primeng/table";
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { Shorty } from '../../../pipes/shorty';
import { PromptTemplateService } from '../../../services/prompt-template-service';

@Component({
  selector: 'app-system-prompts',
  imports: [FormsModule, Breadcrumb, ButtonModule, CheckboxModule, TableModule, TextareaModule, TooltipModule, InputTextModule, ConfirmDialog, Shorty],
  templateUrl: './system-prompts.html',
  styleUrl: './system-prompts.scss',
  providers: [ConfirmationService]
})
export class SystemPrompts implements OnInit {
  @ViewChild('dt') dt!: Table;

  protected kategorie: category | null = null;
  protected promptTemplates: PromptTemplate[] = [];
  protected isLoading: boolean = false;
  protected editingId: string | null = null;
  protected placeholders = promtTemplatePlaceholders;

  private route = inject(ActivatedRoute);
  private promptTemplateService = inject(PromptTemplateService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.kategorie = params.get('kategorie') as category | null;
      if (this.kategorie) {
        this.loadPromptTemplates();
      }
    });
  }

  private async loadPromptTemplates(): Promise<void> {
    if (!this.kategorie) return;

    this.isLoading = true;
    try {
      this.promptTemplates = await this.promptTemplateService.list(this.kategorie);
    } catch (error) {
      console.error('Error loading prompt templates:', error);
      this.promptTemplates = [];
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Prompt-Vorlagen konnten nicht geladen werden' });
    } finally {
      this.isLoading = false;
    }
  }

  protected async addNewPromptTemplate(): Promise<void> {
    if (!this.kategorie) return;

    try {
      const id = await this.promptTemplateService.create('Neuer Prompt', 'Hier kommt der Text des Prompts...', [], this.kategorie);
      await this.loadPromptTemplates();
      this.messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Neuer Prompt erstellt' });
    } catch (error) {
      console.error('Error creating prompt template:', error);
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Prompt konnte nicht erstellt werden' });
    }
  }

  protected async savePromptTemplate(template: PromptTemplate): Promise<void> {
    try {
      await this.promptTemplateService.update({
        _id: template._id,
        name: template.name,
        prompt: template.prompt,
        selectedByDefault: template.selectedByDefault
      });
      this.messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Prompt gespeichert' });
    } catch (error) {
      console.error('Error updating prompt template:', error);
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Prompt konnte nicht gespeichert werden' });
    }
  }

  protected deletePromptTemplate(template: PromptTemplate): void {
    this.confirmationService.confirm({
      message: `Soll der Prompt "${template.name}" wirklich gelöscht werden?`,
      header: 'Löschen bestätigen',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Ja',
      rejectLabel: 'Nein',
      accept: async () => {
        try {
          await this.promptTemplateService.delete(template._id);
          await this.loadPromptTemplates();
          this.messageService.add({ severity: 'success', summary: 'Erfolg', detail: 'Prompt gelöscht' });
        } catch (error) {
          this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Prompt konnte nicht gelöscht werden' });
        }
      }
    });
  }

  protected toggleEdit(id: string): void {
    this.editingId = this.editingId === id ? null : id;
  }

  protected isEditing(id: string): boolean {
    return this.editingId === id;
  }
}
